import { Component } from '@theme/component';
import EmblaCarousel from '@theme/embla';
import { WheelGesturesPlugin } from '@theme/embla-wheel-gestures';

const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

/** A touch must move this far before it counts as a swipe either way */
const SWIPE_DECIDE_PX = 10;

/** A swipe moves the slides only when it is this many times more sideways than down */
const SWIPE_SIDEWAYS_RATIO = 1.5;

/**
 * Content Tile carousel: no autoplay, loops without end, small overlaid
 * prev/next arrows (Biscuit over tiles marked Dark background, Charcoal over
 * others and, from 768, over portrait images on their light frame).
 *
 * Embla (assets/embla-carousel.js) moves the track and loops it; its wheel
 * plugin lets a trackpad's sideways swipe move it too (vertical wheel
 * scrolling stays with the page). On touch
 * the page keeps vertical scrolling: a swipe moves the slides only once it
 * is clearly sideways (#guardSwipes), so a thumb scrolling down the page
 * past a carousel doesn't catch it.
 *
 * Slide images are native lazy (loading="lazy"): the browser fetches each
 * as it comes into the carousel's view.
 */
class NewayContentTileCarousel extends Component {
    /** @type {AbortController} */
    #abortController = new AbortController();

    /** @type {ReturnType<typeof EmblaCarousel> | null} */
    #embla = null;

    connectedCallback() {
        super.connectedCallback();

        const { signal } = this.#abortController;
        const viewport = this.refs.viewport;
        if (!(viewport instanceof HTMLElement)) return;

        const slides = this.#slides();
        this.#embla = EmblaCarousel(viewport, {
            loop: slides.length > 1,
            align: 'start',
            duration: REDUCED_MOTION_QUERY.matches ? 10 : 25,
        }, [WheelGesturesPlugin()]);

        this.#embla.on('select', () => this.#update());
        this.#embla.on('reInit', () => this.#update());

        this.refs.prev?.addEventListener('click', () => this.#embla?.scrollPrev(REDUCED_MOTION_QUERY.matches), { signal });
        this.refs.next?.addEventListener('click', () => this.#embla?.scrollNext(REDUCED_MOTION_QUERY.matches), { signal });

        this.#guardSwipes(viewport, signal);

        this.#update();
    }

    disconnectedCallback() {
        this.#abortController.abort();
        this.#embla?.destroy();
        this.#embla = null;
        super.disconnectedCallback();
    }

    /** The slides, from the refs */
    #slides() {
        return (Array.isArray(this.refs.slide) ? this.refs.slide : [this.refs.slide])
            .filter((s) => s instanceof HTMLElement);
    }

    /**
     * Embla takes a touch for the slides as soon as it has moved further
     * sideways than down, however little. This holds its touchmoves back
     * until the swipe has gone SWIPE_DECIDE_PX; it then reaches Embla only
     * if it is SWIPE_SIDEWAYS_RATIO times more sideways than down, and
     * otherwise stays with the page (touch-action: pan-y) to the end.
     * Embla listens on the viewport, so a capture listener there runs first.
     *
     * @param {HTMLElement} viewport
     * @param {AbortSignal} signal
     */
    #guardSwipes(viewport, signal) {
        let startX = 0;
        let startY = 0;
        /** @type {'pending' | 'slides' | 'page'} */
        let owner = 'pending';

        viewport.addEventListener('touchstart', (e) => {
            const touch = e.touches[0];
            startX = touch.clientX;
            startY = touch.clientY;
            owner = 'pending';
        }, { signal, capture: true, passive: true });

        viewport.addEventListener('touchmove', (e) => {
            if (owner === 'slides') return;
            if (owner === 'pending') {
                const touch = e.touches[0];
                const dx = Math.abs(touch.clientX - startX);
                const dy = Math.abs(touch.clientY - startY);
                if (Math.max(dx, dy) >= SWIPE_DECIDE_PX) {
                    owner = dx > dy * SWIPE_SIDEWAYS_RATIO ? 'slides' : 'page';
                    if (owner === 'slides') return;
                }
            }
            e.stopPropagation();
        }, { signal, capture: true, passive: true });
    }

    /**
     * Marks the slide showing (the arrows take its colour) and, when there
     * is only one slide, the arrows' disabled state.
     */
    #update() {
        const embla = this.#embla;
        if (!embla) return;

        const slides = this.#slides();
        const n = slides.length || 1;
        const index = embla.selectedScrollSnap();

        slides.forEach((slide, i) => slide.toggleAttribute('data-current', i === index));

        // A single image has nothing to move to: no arrows
        this.toggleAttribute('data-single', n < 2);

        this.#setArrowDisabled(this.refs.prev, !embla.canScrollPrev());
        this.#setArrowDisabled(this.refs.next, !embla.canScrollNext());
    }

    /**
     * @param {Element | undefined} arrow
     * @param {boolean} disabled
     */
    #setArrowDisabled(arrow, disabled) {
        if (!(arrow instanceof HTMLElement)) return;
        arrow.setAttribute('aria-disabled', String(disabled));
    }
}

if (!customElements.get('neway-content-tile-carousel')) {
    customElements.define('neway-content-tile-carousel', NewayContentTileCarousel);
}
